import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import "lenis/dist/lenis.css";
import Lenis from "lenis";
import { LazyMotion, domAnimation } from "framer-motion";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer, MobileBar } from "@/components/layout/Footer";
import { setLenis, getLenis } from "@/lib/lenis";
import { PageTracker } from "@/components/PageTracker";
import { LANDING_PAGES } from "@/data/pages";
import Home from "@/pages/Home";

const LandingPage = lazy(() => import("@/pages/LandingPage"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Misc = (name) => lazy(() => import("@/pages/MiscPages").then((m) => ({ default: m[name] })));
const Forms = (name) => lazy(() => import("@/pages/FormPages").then((m) => ({ default: m[name] })));
const ServiceAreasPage = Misc("ServiceAreasPage");
const GalleryPage = Misc("GalleryPage");
const FaqPage = Misc("FaqPage");
const ContactPage = Forms("ContactPage");
const EstimatePage = Forms("EstimatePage");
const ContractorPricingPage = Forms("ContractorPricingPage");

const SmoothScroll = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce), (pointer: coarse)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    setLenis(lenis);
    let id = requestAnimationFrame(function raf(t) {
      lenis.raf(t);
      id = requestAnimationFrame(raf);
    });
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
      setLenis(null);
    };
  }, []);
  useEffect(() => {
    const l = getLenis();
    if (l) l.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Fallback = () => <div className="min-h-[70vh]" aria-busy="true" />;

const Shell = ({ children }) => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return <main id="main">{children}</main>;
  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded focus:bg-white focus:p-3">Skip to content</a>
      <Header />
      <main id="main">{children}</main>
      <Footer />
      <MobileBar />
    </>
  );
};

function App() {
  return (
    <LazyMotion features={domAnimation}>
    <div className="App">
      <BrowserRouter>
        <SmoothScroll />
        <PageTracker />
        <Shell>
          <Suspense fallback={<Fallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
              {LANDING_PAGES.map((p) => (
                <Route key={p.path} path={p.path} element={<LandingPage page={p} />} />
              ))}
              {["/flooring", "/flooring/hardwood", "/flooring/carpet"].map((p) => (
                <Route key={p} path={p} element={<Navigate to="/flooring/luxury-vinyl-plank" replace />} />
              ))}
              <Route path="/installation/*" element={<Navigate to="/installation" replace />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/service-areas" element={<ServiceAreasPage />} />
              <Route path="/service-areas/:slug" element={<CityPage mode="homeowner" />} />
              <Route path="/service-areas/:slug/contractors" element={<CityPage mode="contractor" />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/free-estimate" element={<EstimatePage />} />
              <Route path="/contractor-pricing" element={<ContractorPricingPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Shell>
      </BrowserRouter>
    </div>
    </LazyMotion>
  );
}

export default App;
